import type { TyreAction } from '../tyres.api';

/** Every move a person can record from the screens: the API action, the modal's title and the toast once the server confirms. */
export type TyreMoveKind = 'fit' | 'rotate' | 'wear' | 'mark-worn' | 'repair' | 'retread' | 'remove' | 'scrap' | 'replace';

export const TYRE_MOVE_TITLES: Record<TyreMoveKind, string> = {
  fit: 'Fit a tyre',
  rotate: 'Rotate tyre',
  wear: 'Record wear',
  'mark-worn': 'Mark as worn',
  repair: 'Record puncture repair',
  retread: 'Record retread',
  remove: 'Remove tyre',
  scrap: 'Scrap tyre',
  replace: 'Replace tyre',
};

export const TYRE_MOVE_TOASTS: Record<TyreMoveKind, (serial: string) => string> = {
  fit: (serial) => `${serial} fitted`,
  rotate: (serial) => `${serial} rotated`,
  wear: (serial) => `Wear recorded for ${serial}`,
  'mark-worn': (serial) => `${serial} marked as worn`,
  repair: (serial) => `Repair recorded for ${serial}`,
  retread: (serial) => `Retread recorded for ${serial}`,
  remove: (serial) => `${serial} removed`,
  scrap: (serial) => `${serial} scrapped`,
  replace: (serial) => `${serial} replaced`,
};

export interface TyreMoveFormValues {
  treadDepthMm?: number;
  odometerKm?: number | null;
  notes?: string;
  reason?: string;
  disposition?: 'reusable' | 'worn_out';
  tyreId?: string;
  toPositionCode?: string;
}

export interface TyreMoveContext {
  /** The vehicle and position the move is about (fit, replace, rotate from a vehicle's layout). */
  vehicle?: { kind: 'truck' | 'trailer'; id: string };
  positionCode?: string;
  /** Rotating into an occupied position swaps with the tyre there. */
  swapWithTyreId?: string;
}

interface MoveRequest {
  tyreId: string;
  action: TyreAction;
  body: Record<string, unknown>;
}

type MoveBuilder = (subjectTyreId: string, values: TyreMoveFormValues, context: TyreMoveContext) => MoveRequest;

function common(values: TyreMoveFormValues): Record<string, unknown> {
  return { odometerKm: values.odometerKm ?? undefined, notes: values.notes?.trim() || undefined };
}

/** A move that adds nothing to the odometer and notes every move carries. */
const plain =
  (action: TyreAction): MoveBuilder =>
  (tyreId, values) => ({ tyreId, action, body: common(values) });

/** Which tyre a move is recorded against (the picked one, for a fit) and the body the API's schema takes. */
const BUILDERS: Record<TyreMoveKind, MoveBuilder> = {
  fit: (subject, values, context) => ({
    tyreId: values.tyreId ?? subject,
    action: 'fit',
    body: { ...common(values), vehicleKind: context.vehicle?.kind, vehicleId: context.vehicle?.id, positionCode: context.positionCode },
  }),
  rotate: (tyreId, values, context) => ({
    tyreId,
    action: 'rotate',
    body: { ...common(values), toPositionCode: values.toPositionCode, swapWithTyreId: context.swapWithTyreId },
  }),
  wear: (tyreId, values) => ({ tyreId, action: 'wear', body: { ...common(values), treadDepthMm: values.treadDepthMm } }),
  'mark-worn': plain('mark-worn'),
  repair: plain('repair'),
  retread: plain('retread'),
  remove: (tyreId, values) => ({ tyreId, action: 'remove', body: { ...common(values), reason: values.reason?.trim(), disposition: values.disposition } }),
  scrap: (tyreId, values) => ({ tyreId, action: 'scrap', body: { ...common(values), reason: values.reason?.trim() } }),
  replace: (tyreId, values) => ({
    tyreId,
    action: 'replace',
    body: { ...common(values), withTyreId: values.tyreId, reason: values.reason?.trim(), disposition: values.disposition },
  }),
};

export function buildTyreMove(kind: TyreMoveKind, subjectTyreId: string, values: TyreMoveFormValues, context: TyreMoveContext): MoveRequest {
  return BUILDERS[kind](subjectTyreId, values, context);
}
